import type fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { ORDER_PROJECTS_BY, PROJECT_PICKER_ROWS_PER_PAGE } from '../../../common/ui/constants';
import type {
	ProjectStyleForInput,
	AvatarSizes,
	Page,
	PriorityScheme,
	ProjectInput,
	ProjectPickerPage,
} from '../../../common/ui/types';

type ApiProject = {
	self: string;
	id: string;
	key: string;
	name: string;
	avatarUrls: AvatarSizes;
	projectCategory: {
		self: string;
		id: string;
		name: string;
		description: string;
	};
	simplified: boolean;
	style: (typeof ProjectStyleForInput)[keyof typeof ProjectStyleForInput];
	insight: {
		totalIssueCount: number;
		lastIssueUpdateTime: string;
	};
};

export type ApiResponse = {
	self: string;
	nextPage: string;
	maxResults: number;
	startAt: number;
	total: number;
	isLast: boolean;
	values: ReadonlyArray<ApiProject>;
};

function getSearchParams(startAt: number, query: string): URLSearchParams {
	const projectsParams = new URLSearchParams();
	projectsParams.append('startAt', String(startAt));
	if (query !== '') {
		projectsParams.append('query', query);
	}
	projectsParams.append('maxResults', String(PROJECT_PICKER_ROWS_PER_PAGE));
	projectsParams.append('orderBy', ORDER_PROJECTS_BY);
	return projectsParams;
}

function toProjectInputFactory(isAssociatedToScheme: (project: ApiProject) => boolean) {
	return function toProjectInput(p: ApiProject): ProjectInput {
		return {
			id: p.id,
			name: p.name,
			projectStyle: p.style,
			checked: isAssociatedToScheme(p),
			disabled: false,
			avatarUrl: p.avatarUrls['48x48'],
			isAssociatedWithScheme: isAssociatedToScheme(p),
		};
	};
}

function mapToPage<T>(response: ApiResponse, mapper: (p: ApiProject) => T): Page<T> {
	return {
		pageSize: response.maxResults,
		totalItems: response.total,
		currentPage: response.startAt / response.maxResults + 1,
		items: response.values.map(mapper),
	};
}

function fetchPublicProjectsApi(
	myFetchJson: typeof fetchJson,
	projectsParams: URLSearchParams,
): Promise<ApiResponse> {
	return myFetchJson(`/rest/api/3/project/search?${projectsParams}`);
}

/**
 * Fetches projects from the public API, the one that returns all projects on the instance.
 * It knows nothing about priority scheme associations.
 */
export const fetchAllProjects = async (
	myFetchJson: typeof fetchJson,
	startAt: number,
	query: string,
): Promise<Page<ProjectInput>> => {
	const projectsParams = getSearchParams(startAt, query);
	const response = await fetchPublicProjectsApi(myFetchJson, projectsParams);
	return mapToPage(
		response,
		toProjectInputFactory(() => false),
	);
};

function fetchAssociatedProjectsApi(
	myFetchJson: typeof fetchJson,
	prioritySchemeId: PriorityScheme['id'],
	projectsParams: URLSearchParams,
): Promise<ApiResponse> {
	return myFetchJson(`/rest/api/2/priorityscheme/${prioritySchemeId}/projects?${projectsParams}`);
}

/**
 * Fetches only projects that are associated with the given priority scheme.
 */
export const fetchProjectsAssociatedToPriorityScheme = async (
	myFetchJson: typeof fetchJson,
	prioritySchemeId: PriorityScheme['id'],
	startAt: number,
	query: string,
): Promise<ProjectPickerPage> => {
	const projectsParams = getSearchParams(startAt, query);
	const response = await fetchAssociatedProjectsApi(myFetchJson, prioritySchemeId, projectsParams);
	return mapToPage(
		response,
		toProjectInputFactory(() => true),
	);
};

export const fetchPrioritySchemeProjectSuggestions = async (
	myFetchJson: typeof fetchJson,
	prioritySchemeId: PriorityScheme['id'],
	startAt: number,
	query: string,
): Promise<ProjectPickerPage> => {
	const projectsParams = getSearchParams(startAt, query);
	const projectsResult: ApiResponse = await fetchPublicProjectsApi(myFetchJson, projectsParams);

	const projectIds: string[] = projectsResult.values.map((p) => p.id);
	const associationsParams = new URLSearchParams();
	// Here we always start from 0 because we also pass an array of exactly <PAGE_SIZE> project ids
	associationsParams.append('startAt', String(0));
	associationsParams.append('maxResults', String(projectIds.length));
	projectIds.forEach((id) => associationsParams.append('projectId', id));
	const associationsResponse: ApiResponse = await fetchAssociatedProjectsApi(
		myFetchJson,
		prioritySchemeId,
		associationsParams,
	);
	const associatedIds: Set<ApiProject['id']> = new Set();
	associationsResponse.values.forEach((p) => associatedIds.add(p.id));
	return mapToPage(
		projectsResult,
		toProjectInputFactory((p) => associatedIds.has(p.id)),
	);
};
